import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * role: 'dm' | 'player' | 'display'
 * For 'display', redirects immediately (no login needed).
 */
export default function Login({ role }) {
  const { id: campaignId } = useParams();
  const { auth, loginDm, loginPlayer, logout, error, loading } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [pin,  setPin]  = useState('');
  const [info, setInfo] = useState('');

  // Display view needs no auth — bounce straight in.
  useEffect(() => {
    if (role === 'display') {
      navigate(`/session/${campaignId}/display`, { replace: true });
    }
  }, [role, campaignId, navigate]);

  // Reaching /login while already signed in as the right role: show an explicit
  // "Continue / Switch user" panel instead of silently auto-redirecting. This
  // is the only way for someone on a shared device to actually log in as a
  // different account without first finding the logout button in the game UI.
  const alreadySignedIn = !!auth && auth.role === role && role !== 'display';
  const handleContinue = () => navigate(`/session/${campaignId}/${role}`, { replace: true });
  const handleSwitchUser = () => { logout(); setName(''); setPin(''); setInfo(''); };

  const handleSubmit = async () => {
    setInfo('');
    if (role === 'dm') {
      const result = await loginDm(pin);
      if (result?.ok) {
        if (result.firstRun) setInfo('Campaign created! Welcome, DM.');
        navigate(`/session/${campaignId}/dm`, { replace: true });
      }
    } else {
      const result = await loginPlayer(name, pin);
      if (result?.ok) {
        if (result.firstRun) setInfo('Account created! Welcome to the table.');
        navigate(`/session/${campaignId}/player`, { replace: true });
      }
    }
  };

  const isDm = role === 'dm';

  if (alreadySignedIn) {
    return (
      <div style={styles.screen}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.icon}>{isDm ? '🎲' : '⚔️'}</div>
            <h1 style={styles.title}>Already signed in</h1>
            <p style={styles.subtitle}>
              {isDm ? 'You are logged in as the DM.' : `Continue as ${auth.name || 'this player'}, or switch to a different account.`}
            </p>
          </div>
          <div style={styles.form}>
            <button
              className="btn-primary w-full"
              style={{ marginTop: 8, padding: '12px 0', fontSize: 16 }}
              onClick={handleContinue}
            >
              {isDm ? 'Enter DM View' : `Continue as ${auth.name || 'me'}`}
            </button>
            <button
              className="btn-ghost w-full"
              style={{ marginTop: 4, padding: '10px 0', fontSize: 14 }}
              onClick={handleSwitchUser}
            >
              Switch to a different account
            </button>
          </div>
          <div style={styles.footer}>
            <a href={`/session/${campaignId}/display`} style={styles.link}>Display View →</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.icon}>{isDm ? '🎲' : '⚔️'}</div>
          <h1 style={styles.title}>{isDm ? 'Dungeon Master' : 'Join Session'}</h1>
          <p style={styles.subtitle}>
            {isDm ? 'Enter your master PIN to continue.' : 'Enter your name and PIN.'}
          </p>
        </div>

        {/* Form */}
        <div style={styles.form}>
          {!isDm && (
            <div style={styles.field}>
              <label style={styles.label}>Your Name</label>
              <input
                placeholder="e.g. Aria"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && document.querySelector('#pin-input')?.focus()}
              />
            </div>
          )}
          <div style={styles.field}>
            <label style={styles.label}>{isDm ? 'Master PIN' : 'PIN'}</label>
            <input
              id="pin-input"
              type="password"
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              autoFocus={isDm}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              inputMode="numeric"
              maxLength={16}
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}
          {info  && <p style={styles.success}>{info}</p>}

          <button
            className="btn-primary w-full"
            style={{ marginTop: 8, padding: '12px 0', fontSize: 16 }}
            onClick={handleSubmit}
            disabled={loading || !pin || (!isDm && !name)}
          >
            {loading ? 'Connecting…' : isDm ? 'Enter DM View' : 'Join Session'}
          </button>
        </div>

        {/* Switch role links */}
        <div style={styles.footer}>
          {isDm ? (
            <a href={`/session/${campaignId}/player/login`} style={styles.link}>Join as a Player instead</a>
          ) : (
            <a href={`/session/${campaignId}/dm/login`} style={styles.link}>DM Login</a>
          )}
          <span style={styles.sep}>·</span>
          <a href={`/session/${campaignId}/display`} style={styles.link}>Display View →</a>
        </div>

        {isDm && (
          <p style={styles.note}>
            First time? Set any PIN — it becomes the master PIN for this campaign.
          </p>
        )}
      </div>
    </div>
  );
}

const styles = {
  screen: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'radial-gradient(ellipse at 50% 0%, #162844 0%, #0a0f1e 70%)',
    padding: 16,
  },
  card: {
    background: 'linear-gradient(180deg, #1a2a48 0%, #142038 100%)',
    border: '2px solid #8b7340',
    borderRadius: 6,
    padding: '36px 32px',
    width: '100%',
    maxWidth: 400,
    boxShadow: '0 20px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(196,164,74,0.15)',
  },
  header: {
    textAlign: 'center',
    marginBottom: 28,
  },
  icon: {
    fontSize: 48,
    marginBottom: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    fontFamily: "'Press Start 2P', 'Palatino Linotype', monospace",
    color: '#d4a847',
    marginBottom: 6,
    textShadow: '0 2px 8px rgba(212,168,71,0.3)',
  },
  subtitle: {
    color: '#9a8b6e',
    fontSize: 14,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'Press Start 2P', monospace",
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#9a8b6e',
  },
  error: {
    color: '#e07070',
    fontSize: 13,
    background: 'rgba(192,57,43,0.15)',
    padding: '8px 12px',
    borderRadius: 4,
    border: '1px solid rgba(192,57,43,0.4)',
  },
  success: {
    color: '#5cb870',
    fontSize: 13,
    background: 'rgba(74,154,90,0.15)',
    padding: '8px 12px',
    borderRadius: 4,
    border: '1px solid rgba(74,154,90,0.4)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    fontSize: 13,
  },
  link: {
    color: '#d4a847',
    textDecoration: 'none',
    opacity: 0.8,
  },
  sep: {
    color: '#4a3d28',
  },
  note: {
    marginTop: 16,
    fontSize: 12,
    color: '#6a5d45',
    textAlign: 'center',
    borderTop: '1px solid #4a3d28',
    paddingTop: 16,
  },
};
